/**
 * PreservedReturnPage — /preserved/:slug
 *
 * The post-payment return surface. Per Craig: "the most important
 * surface honestly… the return page should feel like the night has
 * now been entrusted to preservation. Not 'payment successful ✓'."
 *
 * Reads:
 *   "This night now has a place to return to ✦"
 *
 * with the wall preview, the archive awaiting its first artifact,
 * and the next-step affordances. The single moment that changes the
 * emotional framing of the entire transaction.
 *
 * Tone constraints:
 *   • Calm confidence — Mode 1-adjacent, NOT a cinematic showcase
 *   • NO success burst, NO checkmark, NO "payment received" copy
 *   • NO Stripe brand surfacing
 *   • NO toast notifications
 *   • Slow, breathing acknowledgement — the night has been entrusted
 */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import EntrustedVeil from '../components/EntrustedVeil';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const IVORY = '#FBEED0';
const GOLD = '#D4AF5E';
const GOLD_MUTED = 'rgba(212,170,82,0.78)';
const INK_DIM = 'rgba(255,235,200,0.62)';
const WALNUT = '#0B0604';

const EDITION_WHISPER = {
  // Acknowledges intent, never spend. A whispered preparation happening
  // elsewhere in the building — never "you bought X" / "upgrade" / "premium".
  keepsake: 'The first envelope is on its way to the morning ✦',
  room:     'The room is beginning to glow ✦',
  archive:  'The bookshelf is being prepared for its first chapter ✦',
};

function _editionKey(label) {
  if (!label) return null;
  const s = label.toLowerCase();
  if (s.includes('keepsake')) return 'keepsake';
  if (s.includes('room')) return 'room';
  if (s.includes('archive')) return 'archive';
  return null;
}

export default function PreservedReturnPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [editionLabel, setEditionLabel] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  // The veil opens with the page (carry-through from Stripe) and
  // softly dissolves over ~320ms — completing the ceremonial loop:
  // entrusted → preserved. Only fires when arriving from a Stripe
  // redirect (a session_id is present), so direct deep-links to
  // /preserved/:slug never carry a phantom veil.
  const [veilOpen, setVeilOpen] = useState(Boolean(searchParams.get('session_id')));

  // Dissolve the veil shortly after mount — gives one paint frame
  // for the parchment to register, then fades into the preserved
  // surface.
  useEffect(() => {
    if (!veilOpen) return undefined;
    const t = setTimeout(() => setVeilOpen(false), 220);
    return () => clearTimeout(t);
  }, [veilOpen]);

  // Load the event by slug + poll the payment status if a session_id
  // is present. The page sits in a soft "settling" state while the
  // webhook flips the order — never a spinner, never a progress bar.
  // iter215 — also runs the booking-recovery poll on /checkout/status
  // when the slug lookup fails so a host who lost their event row mid-
  // Stripe-redirect still sees a calm "finalising your booking" state
  // instead of a "no payment session" dead end.
  const [recoveryState, setRecoveryState] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (!cancelled) setEvent(r.data);
      } catch { /* silent — the page still reads as a return surface */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId || event) return undefined;
    // Event row hasn't loaded yet — start the recovery poll. The
    // server will either reconstruct the booking from Stripe metadata
    // (returning status=complete + an event) or report status=
    // "finalising" until the payment lands. We surface this as a
    // gentle whisper, never a spinner.
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(
          `${API}/checkout/status/${encodeURIComponent(sessionId)}`,
        );
        if (!cancelled) setRecoveryState(r.data);
        if (r.data?.status === 'complete' && r.data?.event?.slug) {
          // Recovered. Land the host on the canonical /preserved page
          // for their reconstructed slug.
          if (r.data.event.slug !== slug) {
            window.history.replaceState(
              {},
              '',
              `/preserved/${r.data.event.slug}?session_id=${sessionId}`,
            );
          }
          // Trigger the slug lookup again on the next tick.
          try {
            const ev = await axios.get(`${API}/events/slug/${r.data.event.slug}`);
            if (!cancelled) setEvent(ev.data);
          } catch { /* silent */ }
          return;
        }
      } catch { /* keep waiting */ }
      tries++;
      if (tries < 40 && !cancelled) setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [searchParams, slug, event]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const edition = searchParams.get('edition');
    if (edition) setEditionLabel(edition);
    if (!sessionId || !event?.event_id) return;
    let cancelled = false;
    let tries = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API}/events/${event.event_id}/editions/status?session_id=${encodeURIComponent(sessionId)}`);
        if (r.data?.confirmed) {
          if (!cancelled) setConfirmed(true);
          return;
        }
      } catch { /* keep waiting */ }
      tries++;
      if (tries < 30 && !cancelled) setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [searchParams, event]);

  return (
    <div data-testid="preserved-return-page" style={{ background: WALNUT, color: IVORY, minHeight: '100vh' }}>
      <style>{`
        @keyframes preservedBreathe {
          0%, 100% { box-shadow: 0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(212,170,82,0.10) inset; }
          50%      { box-shadow: 0 32px 90px rgba(0,0,0,0.62), 0 0 0 1px rgba(212,170,82,0.18) inset; }
        }
        @keyframes preservedDust {
          0%   { transform: translate(0,0); opacity: 0; }
          15%  { opacity: 0.6; }
          50%  { transform: translate(38vw,-12px); opacity: 0.5; }
          85%  { opacity: 0; }
          100% { transform: translate(82vw,-8px); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="preserved-return-page"] * { animation: none !important; }
        }
      `}</style>

      <header style={{
        padding: '20px 28px',
        borderBottom: '1px solid rgba(212,170,82,0.08)',
        background: 'rgba(11,6,4,0.6)',
        backdropFilter: 'blur(14px)',
      }}>
        <Link to="/" style={{
          fontFamily: SERIF, fontSize: 22, color: IVORY,
          textDecoration: 'none', letterSpacing: '-0.005em',
        }}>EventDrop ✦</Link>
      </header>

      <main style={{
        position: 'relative',
        padding: 'clamp(80px, 12vw, 160px) clamp(24px, 6vw, 80px) clamp(60px, 8vw, 110px)',
        textAlign: 'center',
        overflow: 'hidden',
      }}>
        {/* gentle dust motes — barely present */}
        {[0, 7, 14].map((d, i) => (
          <span key={i} aria-hidden style={{
            position: 'absolute', top: `${22 + i * 18}%`, left: '6%',
            width: 3, height: 3, borderRadius: 999,
            background: 'rgba(255,224,176,0.40)',
            filter: 'blur(0.6px)',
            animation: `preservedDust 26s linear ${d}s infinite`,
            pointerEvents: 'none',
          }} />
        ))}

        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
          color: GOLD_MUTED, marginBottom: 16,
        }}>
          {editionLabel
            ? `${editionLabel.toUpperCase()} ✦ ENTRUSTED`
            : 'A PRESERVATION HAS BEEN COMMISSIONED ✦'}
        </div>
        <h1 style={{
          margin: 0,
          fontFamily: SERIF,
          fontSize: 'clamp(32px, 5.4vw, 60px)',
          fontWeight: 500, lineHeight: 1.12,
          letterSpacing: '-0.018em',
          color: IVORY,
          textShadow: '0 0 32px rgba(212,170,82,0.20)',
          maxWidth: 780, marginInline: 'auto',
        }}>
          This night now has a place to return to ✦
        </h1>
        <EditionWhisper
          editionLabel={editionLabel}
          extraDelayMs={searchParams.get('session_id') ? 540 : 0}
        />
        <p style={{
          margin: '26px auto 0', maxWidth: 580,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(15px, 1.3vw, 17px)',
          lineHeight: 1.65,
          color: INK_DIM,
        }}>
          {event?.event_name ? <>The wall for <b style={{ color: IVORY, fontWeight: 400 }}>{event.event_name}</b> is awake. The archive shelf is ready to receive its first artifact. The recap letter will arrive on its own, the morning after.</>
            : 'The wall is awake. The archive shelf is ready to receive its first artifact. The recap letter will arrive on its own, the morning after.'}
        </p>

        {!confirmed && searchParams.get('session_id') && (
          <div style={{
            margin: '36px auto 0',
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 12.5, color: 'rgba(212,170,82,0.66)',
            letterSpacing: '0.18em', textTransform: 'uppercase',
          }}>
            The night is settling in ✦
          </div>
        )}

        {/* iter215 — booking recovery whisper. Shown only while we are
            waiting for the recovery path to reconstruct the event row,
            so the host never sees "No payment session found" after
            their card has been charged. */}
        {!event && recoveryState && (
          <div
            data-testid="booking-recovery-whisper"
            style={{
              margin: '36px auto 0', maxWidth: 520,
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 14, color: 'rgba(255,235,200,0.74)',
              lineHeight: 1.6,
            }}
          >
            Your payment was successful. We're finalising your booking ✦
          </div>
        )}
      </main>

      {event && <WallVignette event={event} />}
      <NextSteps event={event} />
      <ArchiveTeaser event={event} />
      <Footer />
      <EntrustedVeil open={veilOpen} fadeMs={320} />
    </div>
  );
}

// ── Wall vignette — a small living preview ─────────────────────────
// EditionWhisper — a single italic-serif line beneath the headline
// that quietly acknowledges INTENT, not spend.
//
// Behaviour:
//   • fades in slower than the headline (1.6s base delay + 1.2s ease)
//   • when the entrusted veil dissolved on arrival, the entrance is
//     delayed by an additional `extraDelayMs` so the whisper arrives
//     into a fully-settled preserved surface — discovered, not
//     concurrent with the dissolve. Separating the beats turns "a
//     designed interface" into "a felt sequence".
//   • single-line, italic serif, 68% ivory opacity, no divider
//   • discovered, not announced — feels like a whispered preparation
//     happening elsewhere in the building
//   • NEVER mentions package names, pricing, "upgrade", "premium",
//     "included", "features unlocked"
const EditionWhisper = ({ editionLabel, extraDelayMs = 0 }) => {
  const key = _editionKey(editionLabel);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!key) return undefined;
    const t = setTimeout(() => setVisible(true), 1600 + extraDelayMs);
    return () => clearTimeout(t);
  }, [key, extraDelayMs]);
  if (!key) return null;
  return (
    <div
      data-testid="preserved-edition-whisper"
      data-edition-key={key}
      style={{
        margin: '16px auto 0',
        maxWidth: 560,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 'clamp(13.5px, 1.15vw, 15.5px)',
        lineHeight: 1.55,
        color: 'rgba(255,235,200,0.68)',
        letterSpacing: '0.01em',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(3px)',
        transition: 'opacity 1200ms ease-out, transform 1200ms ease-out',
      }}
    >
      {EDITION_WHISPER[key]}
    </div>
  );
};

const WallVignette = ({ event }) => (
  <section style={{
    padding: '0 clamp(24px, 6vw, 80px) clamp(60px, 7vw, 90px)',
  }}>
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <Link
        to={`/event/${event.slug}/memory-wall`}
        data-testid="preserved-go-to-wall"
        style={{
          display: 'block',
          position: 'relative',
          aspectRatio: '16 / 9',
          background:
            'linear-gradient(160deg, #1A0F08 0%, #0A0604 100%)',
          border: '1px solid rgba(212,170,82,0.20)',
          borderRadius: 4,
          overflow: 'hidden',
          textDecoration: 'none',
          animation: 'preservedBreathe 8s ease-in-out infinite',
        }}
      >
        {/* projected warm glow */}
        <div aria-hidden style={{
          position: 'absolute', top: '14%', left: '22%', right: '22%', bottom: '24%',
          background: 'radial-gradient(ellipse at 50% 50%, rgba(255,222,168,0.22) 0%, transparent 65%)',
        }} />
        {/* fairy bulbs */}
        <div aria-hidden style={{
          position: 'absolute', top: 22, left: 0, right: 0,
          display: 'flex', justifyContent: 'space-around', padding: '0 14%',
        }}>
          {[0,1,2,3,4,5,6].map((i) => (
            <span key={i} style={{
              width: 5, height: 5, borderRadius: 999,
              background: 'rgba(255,222,168,0.85)',
              boxShadow: '0 0 10px 4px rgba(255,206,144,0.5)',
            }} />
          ))}
        </div>
        <div style={{
          position: 'absolute', bottom: 24, left: 24,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase',
          color: 'rgba(212,170,82,0.82)',
        }}>
          OPEN THE WALL ✦
        </div>
        <div style={{
          position: 'absolute', bottom: 22, right: 24,
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 12, color: 'rgba(255,235,200,0.66)',
        }}>
          Tap to step inside →
        </div>
      </Link>
    </div>
  </section>
);

// ── Next steps — restrained, three quiet affordances ───────────────
const NextSteps = ({ event }) => {
  if (!event) return null;
  const steps = [
    {
      to: `/event/${event.slug}/memory-wall?show_qr=1`,
      eyebrow: 'BRING THE ROOM IN ✦',
      title: 'Open the share QR',
      sub: 'Everyone scans the same code. The wall starts breathing within minutes.',
      testId: 'preserved-next-qr',
    },
    {
      to: `/event/${event.slug}/archive`,
      eyebrow: 'THE SHELF AWAITS ✦',
      title: 'Open the archive',
      sub: 'A quiet place that fills in as the night gives it things to keep.',
      testId: 'preserved-next-archive',
    },
    {
      to: `/event/${event.slug}`,
      eyebrow: "THE HOST'S DESK ✦",
      title: 'Open the desk',
      sub: 'Where you decide what gets kept, what stays loud, and how the night is remembered.',
      testId: 'preserved-next-dashboard',
    },
  ];
  return (
    <section style={{
      background: '#0C0704',
      borderTop: '1px solid rgba(212,170,82,0.10)',
      padding: 'clamp(60px, 8vw, 100px) clamp(24px, 6vw, 80px)',
    }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
          color: GOLD_MUTED, marginBottom: 12, textAlign: 'center',
        }}>
          THE NEXT QUIET MOVES ✦
        </div>
        <h2 style={{
          margin: 0, textAlign: 'center',
          fontFamily: SERIF,
          fontSize: 'clamp(22px, 3vw, 30px)',
          fontWeight: 500, color: IVORY,
          letterSpacing: '-0.01em',
          marginBottom: 38,
        }}>
          When you are ready, take one
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 'clamp(18px, 2vw, 26px)',
        }}>
          {steps.map((s) => (
            <Link key={s.to} to={s.to} data-testid={s.testId}
              style={{
                display: 'block',
                background: 'linear-gradient(180deg, rgba(20,12,6,0.96) 0%, rgba(12,7,4,0.96) 100%)',
                border: '1px solid rgba(212,170,82,0.18)',
                borderRadius: 4,
                padding: '22px 22px 24px',
                color: IVORY, textDecoration: 'none',
                transition: 'transform 220ms ease, border-color 220ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212,170,82,0.40)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(212,170,82,0.18)';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <div style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 10.5, letterSpacing: '0.26em', textTransform: 'uppercase',
                color: GOLD_MUTED, marginBottom: 10,
              }}>{s.eyebrow}</div>
              <div style={{
                fontFamily: SERIF, fontSize: 20,
                color: IVORY, marginBottom: 8,
                letterSpacing: '-0.005em',
              }}>{s.title} →</div>
              <div style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 13.5, lineHeight: 1.55,
                color: INK_DIM,
              }}>{s.sub}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Archive teaser — a single empty parchment card waiting ─────────
const ArchiveTeaser = ({ event }) => (
  <section style={{
    padding: 'clamp(60px, 8vw, 100px) clamp(24px, 6vw, 80px)',
    textAlign: 'center',
  }}>
    <div style={{
      maxWidth: 540, margin: '0 auto',
      background: 'linear-gradient(180deg, rgba(248,238,214,0.94) 0%, rgba(240,224,192,0.94) 100%)',
      border: '1px solid rgba(168,133,76,0.30)',
      borderRadius: 4,
      padding: '36px 28px 38px',
      boxShadow: '0 22px 50px rgba(0,0,0,0.45)',
    }}>
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 10.5, letterSpacing: '0.26em', textTransform: 'uppercase',
        color: 'rgba(168,133,76,0.85)', marginBottom: 14,
      }}>
        THE ARCHIVE SHELF ✦
      </div>
      <div style={{
        fontFamily: SERIF,
        fontSize: 'clamp(20px, 2.2vw, 26px)',
        color: '#3a2412', lineHeight: 1.25,
        marginBottom: 12, letterSpacing: '-0.008em',
      }}>
        Ready to receive its first artifact
      </div>
      <p style={{
        margin: '0 auto', maxWidth: 420,
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 14, lineHeight: 1.6,
        color: 'rgba(58,36,14,0.66)',
      }}>
        The first chapter will compose itself once a memory finds the wall.
        Letters will appear here over time. The shelf grows in its own way.
      </p>
      {event && (
        <div style={{ marginTop: 22 }}>
          <Link to={`/event/${event.slug}/archive`}
            data-testid="preserved-archive-link"
            style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 13.5, color: '#7a4a18',
              textDecoration: 'underline', textDecorationColor: 'rgba(168,133,76,0.42)',
              textUnderlineOffset: 4,
            }}>
            Visit the shelf ✦
          </Link>
        </div>
      )}
    </div>
  </section>
);

const Footer = () => (
  <footer style={{
    background: '#0A0503',
    padding: '52px 28px',
    borderTop: '1px solid rgba(212,170,82,0.10)',
    textAlign: 'center',
  }}>
    <div style={{
      fontFamily: SERIF, fontSize: 20, color: IVORY,
      letterSpacing: '-0.005em',
    }}>EventDrop ✦</div>
    <div style={{
      marginTop: 10,
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 12, color: 'rgba(255,235,200,0.46)',
    }}>
      Made with care · for the nights worth keeping · © {new Date().getFullYear()}
    </div>
  </footer>
);

// Keep linter happy about unused import shape.
// eslint-disable-next-line no-unused-vars
const _unused = GOLD;
